/**
 * The catalogue of app screenshots the page may use.
 *
 * Every entry here exists on disk under public/shots as AVIF + WebP + JPEG in the widths
 * listed below; the files and these numbers are produced by the same script
 * (landing/tools/build-shots.mjs, `npm run shots`) and catalogued in
 * landing/todo/50-screenshots.md.
 *
 * The intrinsic width/height are MANDATORY and are the reason this table exists rather than
 * a string in each section: without them the page reflows as each frame arrives, and a
 * marketing page loads its images last. They are the dimensions of the DEFAULT variant (the
 * middle width), which is also what `src` points at for a browser that understands neither
 * AVIF nor WebP nor srcset.
 *
 * Only dark-theme frames are published — one set, by an owner decision: a dark frame inside
 * a bezel reads correctly on either page theme, and the page follows the visitor's system
 * preference rather than forcing one.
 *
 * NOT LISTED ON PURPOSE: `settings-appearance` (desktop). Its THEME control highlights
 * "Light" while the interface around it is dark, so the frame contradicts itself. The
 * Android equivalent (`phone-appearance`) does not have that problem and is listed.
 */

export type ShotKind = "desktop" | "phone";

export interface Shot {
  /** File stem under /shots — `chat-desktop` resolves to `/shots/chat-desktop-1100.avif` etc. */
  readonly name: string;
  readonly kind: ShotKind;
  /** Intrinsic size of the default variant, in CSS pixels. */
  readonly width: number;
  readonly height: number;
}

/** The widths each kind was rendered at. The middle one is the default variant. */
const WIDTHS: Record<ShotKind, readonly number[]> = {
  desktop: [720, 1100, 1600],
  phone: [300, 420, 591],
};

const DEFAULT_WIDTH: Record<ShotKind, number> = { desktop: 1100, phone: 420 };

/**
 * `sizes` per kind. A desktop frame spans the content column; a phone frame is one of three
 * in a row on a wide screen and one of three narrower ones on a small screen.
 */
const SIZES: Record<ShotKind, string> = {
  desktop: "(max-width: 1160px) 92vw, 1040px",
  phone: "(max-width: 720px) 44vw, 260px",
};

function desktop(name: string, height: number): Shot {
  return { name, kind: "desktop", width: 1100, height };
}

function phone(name: string): Shot {
  // Every phone frame was cropped to the same box (status bar and navigation bar removed),
  // so the geometry is shared rather than repeated per entry.
  return { name, kind: "phone", width: 420, height: 817 };
}

export const SHOTS = {
  chatDesktop: desktop("chat-desktop", 536),
  groupCall: desktop("group-call", 532),
  folders: desktop("folders", 534),
  settingsPrivacy: desktop("settings-privacy", 527),
  settingsProfile: desktop("settings-profile", 534),
  phoneChat: phone("phone-chat"),
  phoneChats: phone("phone-chats"),
  phoneCall: phone("phone-call"),
  phonePrivacy: phone("phone-privacy"),
  phoneProfile: phone("phone-profile"),
  phoneAppearance: phone("phone-appearance"),
  /**
   * ⚠ PENDING OWNER CONFIRMATION. This frame shows a live invite code and QR from the demo
   * account on stage. It is referenced from exactly one place (the platforms gallery), so
   * swapping it is a single-line change: point this constant at another phone frame, e.g.
   * `phone("phone-profile")`, and nothing else moves.
   */
  phoneInvite: phone("phone-invite"),
} as const satisfies Record<string, Shot>;

export function srcSet(shot: Shot, extension: "avif" | "webp"): string {
  return WIDTHS[shot.kind]
    .map((width) => `/shots/${shot.name}-${width}.${extension} ${width}w`)
    .join(", ");
}

export function fallbackSrc(shot: Shot): string {
  return `/shots/${shot.name}-${DEFAULT_WIDTH[shot.kind]}.jpg`;
}

export function sizesFor(shot: Shot): string {
  return SIZES[shot.kind];
}
